'use client';

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { PenTool } from 'lucide-react';
import { asObject, type FieldValue } from '../formFieldTypes';

// Lazy load the heavy SignatureCapture component (it pulls in QR, GPS, contacts, etc.)
const LazySignatureCapture = React.lazy(() =>
  import('@/app/client-zone/modules/total-operations/components/smart-task/components/SignatureCapture').then((m) => ({
    default: m.default,
  }))
);

/**
 * SignaturePadField — Touch/mouse-based signature capture
 *
 * Canvas-based drawing with optional printed name and designation.
 * When `workItemId` is provided, an enhanced SignatureCapture modal is available
 * with GPS tagging, QR scan mode, and contact-person selection.
 *
 * options.signer_role         — "Customer" | "Technician" | "Supervisor"
 * options.require_name        — Also capture printed name
 * options.require_id_number   — SA ID number
 * options.require_designation — Professional title
 */

type SignaturePadOptions = {
      signer_role?: string;
      require_name?: boolean;
      require_id_number?: boolean;
      require_designation?: boolean;
      [key: string]: unknown;
};

interface SignaturePadFieldProps {
  field: {
    key: string;
    label: string;
    required?: boolean;
    readonly?: boolean;
    options?: unknown;
    help_text?: string;
    [key: string]: unknown;
  };
  value: FieldValue; // { data_url, signer_name?, id_number?, designation?, timestamp }
  onChange: (value: FieldValue) => void;
  isDark: boolean;
  /** When provided, enables the enhanced SignatureCapture modal (GPS, QR, contacts) */
  workItemId?: string;
}

interface SignatureData {
  data_url: string;
  signer_name?: string;
  id_number?: string;
  designation?: string;
  signer_role?: string;
  timestamp: string;
  [key: string]: unknown;
}

export const SignaturePadField: React.FC<SignaturePadFieldProps> = ({ field, value, onChange, isDark, workItemId }) => {
  const opts = (field.options ?? {}) as SignaturePadOptions;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);

  const signerRole = opts?.signer_role || '';
  const requireName = opts?.require_name ?? false;
  const requireIdNumber = opts?.require_id_number ?? false;
  const requireDesignation = opts?.require_designation ?? false;

  const sigData: SignatureData | null = asObject(value)?.data_url ? (value as SignatureData) : null;

  // Initialize state from existing value
  const [hasSignature, setHasSignature] = useState(() => !!sigData);
  const [signerName, setSignerName] = useState(() => sigData?.signer_name || '');
  const [idNumber, setIdNumber] = useState(() => sigData?.id_number || '');
  const [designation, setDesignation] = useState(() => sigData?.designation || '');

  // Draw existing signature on canvas when sigData changes
  useEffect(() => {
    if (canvasRef.current && sigData?.data_url) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = sigData.data_url;
      }
    }
  }, [sigData?.data_url]);

  // Setup canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas resolution
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = isDark ? '#e5e7eb' : '#1f2937';

    // Re-draw existing signature if present
    if (sigData?.data_url) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = sigData.data_url;
    }
  }, [isDark, sigData?.data_url]);

  const getPos = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (field.readonly) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasSignature(true);
  }, [field.readonly, getPos]);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || field.readonly) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }, [isDrawing, field.readonly, getPos]);

  const commitSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dataUrl = canvas.toDataURL('image/png');
    onChange({
      data_url: dataUrl,
      signer_name: signerName || undefined,
      id_number: idNumber || undefined,
      designation: designation || undefined,
      signer_role: signerRole || undefined,
      timestamp: new Date().toISOString(),
    });
  }, [signerName, idNumber, designation, signerRole, onChange]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    commitSignature();
  }, [isDrawing, commitSignature]);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, rect.width * dpr, rect.height * dpr);
    setHasSignature(false);
    onChange(null);
  }, [onChange]);

  const handleMetaChange = useCallback((updates: Partial<SignatureData>) => {
    if (sigData) {
      onChange({ ...sigData, ...updates });
    }
    if (updates.signer_name !== undefined) setSignerName(updates.signer_name || '');
    if (updates.id_number !== undefined) setIdNumber(updates.id_number || '');
    if (updates.designation !== undefined) setDesignation(updates.designation || '');
  }, [sigData, onChange]);

  // Enhanced SignatureCapture modal handler — maps result to form field format
  const handleCaptureResult = useCallback((result: {
    data_url: string;
    signer_name: string;
    signer_role?: string;
    designation?: string;
    signed_at: string;
    location?: { latitude: number; longitude: number; accuracy: number };
    work_item_id: string;
    qr_code_data?: string;
  }) => {
    onChange({
      data_url: result.data_url,
      signer_name: result.signer_name || undefined,
      designation: result.designation || undefined,
      signer_role: result.signer_role || signerRole || undefined,
      timestamp: result.signed_at,
      gps: result.location ? {
        lat: result.location.latitude,
        lng: result.location.longitude,
        accuracy: result.location.accuracy,
      } : undefined,
      qr_code_data: result.qr_code_data,
      work_item_id: result.work_item_id,
    });
    setHasSignature(true);
    setSignerName(result.signer_name || '');
    setDesignation(result.designation || '');
    setShowCaptureModal(false);
  }, [onChange, signerRole]);

  const inputClasses = `block w-full rounded-md px-3 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-teal-500 ${
    isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div className="space-y-3">
      {/* Signer role label */}
      {signerRole && (
        <div className={`text-xs font-medium px-2 py-1 rounded inline-block ${isDark ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
          {signerRole} Signature
        </div>
      )}

      {/* Enhanced capture button — opens SignatureCapture modal when workItemId is available */}
      {workItemId && !field.readonly && (
        <button
          type="button"
          onClick={() => setShowCaptureModal(true)}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isDark
              ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60 border border-blue-700'
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
          }`}
        >
          <PenTool className="w-4 h-4" />
          Enhanced Signature Capture
        </button>
      )}

      {/* Canvas */}
      <div className={`relative rounded-lg border-2 ${
        hasSignature
          ? 'border-teal-400 dark:border-teal-600'
          : isDark ? 'border-gray-600 border-dashed' : 'border-gray-300 border-dashed'
      }`}>
        <canvas
          ref={canvasRef}
          className={`w-full rounded-lg cursor-crosshair touch-none ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}
          style={{ height: '120px' }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && (
          <div className={`absolute inset-0 flex items-center justify-center pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            <span className="text-sm">Sign here</span>
          </div>
        )}
        {hasSignature && !field.readonly && (
          <button
            type="button"
            onClick={clearSignature}
            className="absolute top-2 right-2 px-2 py-1 text-xs rounded bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Name / ID / Designation fields */}
      {(requireName || requireIdNumber || requireDesignation) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {requireName && (
            <input
              type="text"
              value={signerName}
              onChange={(e) => handleMetaChange({ signer_name: e.target.value })}
              placeholder={`Printed name${signerRole ? ` (${signerRole})` : ''}`}
              className={inputClasses}
              required={requireName}
              readOnly={field.readonly}
            />
          )}
          {requireIdNumber && (
            <input
              type="text"
              value={idNumber}
              onChange={(e) => handleMetaChange({ id_number: e.target.value })}
              placeholder="ID / License number"
              className={inputClasses}
              required={requireIdNumber}
              readOnly={field.readonly}
            />
          )}
          {requireDesignation && (
            <input
              type="text"
              value={designation}
              onChange={(e) => handleMetaChange({ designation: e.target.value })}
              placeholder="Designation / Title"
              className={inputClasses}
              required={requireDesignation}
              readOnly={field.readonly}
            />
          )}
        </div>
      )}

      {/* Timestamp */}
      {sigData?.timestamp && (
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Signed: {new Date(sigData.timestamp).toLocaleString()}
        </p>
      )}

      {/* Enhanced SignatureCapture modal — lazy loaded when workItemId is available */}
      {workItemId && showCaptureModal && (
        <React.Suspense fallback={<div className="flex items-center justify-center p-8 text-sm text-gray-500">Loading signature capture…</div>}>
          <LazySignatureCapture
            isOpen={showCaptureModal}
            workItemId={workItemId}
            gpsLocation={null}
            signerRole={signerRole || 'Customer'}
            onCapture={handleCaptureResult}
            onCancel={() => setShowCaptureModal(false)}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export default SignaturePadField;
